import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  useMediaQuery,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  Paper,
  Chip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";
import { useTheme } from "@mui/material/styles";

import { useStyle } from "../../context/styleContext/styleContext";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
import HomePageProductCard from "../../components/ProductCard/HomePageProductCard";
import Seo from "../../components/Seo";

function ProductListSchemaMarkup({ products }) {
  useEffect(() => {
    if (!products || products.length === 0) return;

    const schemaData = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "Parish Audio Plugins",
      url: "https://parishaudio.com/plugins",
      mainEntity: {
        "@type": "ItemList",
        itemListElement: products.map((product, index) => ({
          "@type": "ListItem",
          position: index + 1,
          url: `https://parishaudio.com/products/${product.productURI}`,
          name: product.name,
        })),
      },
    };

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.innerHTML = JSON.stringify(schemaData);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, [products]);

  return null;
}

export default function Plugins() {
  const [height, setHeight] = useState(window.innerHeight);
  const [dataForAllProducts, setDataForAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("featured");

  const { style } = useStyle();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    function handleResize() {
      setHeight(window.innerHeight);
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  const getProductInfo = async () => {
    try {
      setLoading(true);
      const { data } = await axios.post("/api/get-products-available-on-website");

      if (data) {
        setDataForAllProducts(data);
      } else {
        console.log("data error");
      }
    } catch (error) {
      console.log("Error retrieving data...", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getProductInfo();
  }, []);

  const filteredProducts = useMemo(() => {
    let products = [...dataForAllProducts];

    const query = searchTerm.trim().toLowerCase();
    if (query) {
      products = products.filter((product) => {
        const searchableText = [
          product.name,
          product.shortDescription,
          product.cardSummary,
          product.longDescription,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return searchableText.includes(query);
      });
    }

    if (sortBy === "name-asc") {
      products.sort((a, b) => a.name.localeCompare(b.name));
    } else if (sortBy === "name-desc") {
      products.sort((a, b) => b.name.localeCompare(a.name));
    } else if (sortBy === "price-low") {
      products.sort((a, b) => Number(a.price) - Number(b.price));
    } else if (sortBy === "price-high") {
      products.sort((a, b) => Number(b.price) - Number(a.price));
    } else if (sortBy === "rating") {
      products.sort((a, b) => {
        const aRating = Number(a.starRating || 0);
        const bRating = Number(b.starRating || 0);
        const aCount = Number(a.ratingCount || 0);
        const bCount = Number(b.ratingCount || 0);

        if (bRating !== aRating) return bRating - aRating;
        return bCount - aCount;
      });
    } else {
      products.sort((a, b) => {
        if (a.productURI === "d1-delay") return -1;
        if (b.productURI === "d1-delay") return 1;
        return 0;
      });
    }

    return products;
  }, [dataForAllProducts, searchTerm, sortBy]);

  return (
    <>
      <Seo
        title="Audio Plugins | Parish Audio"
        description="Browse Parish Audio plugins for Mac and Windows. Professional audio tools designed for real-world recording, mixing, and tone shaping."
        canonical="https://parishaudio.com/plugins"
        image="https://parishaudio.com/assets/HeroBanner1.png"
        imageAlt="Parish Audio plugin collection"
        ogType="website"
      />

      {filteredProducts.length > 0 && (
        <ProductListSchemaMarkup products={filteredProducts} />
      )}

      <Box
        sx={{
          width: "100%",
          maxWidth: "100vw",
          overflowX: "hidden",
          backgroundColor: "black",
          px: { xs: 1.25, sm: 1.5, md: 2.5 },
          py: { xs: 1.25, md: 2 },
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: "1120px",
            margin: "0 auto",
            minHeight: height - 130,
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
          }}
        >
          <Stack spacing={{ xs: 1.5, md: 2 }}>
            <Paper
              elevation={0}
              sx={{
                width: "100%",
                minWidth: 0,
                borderRadius: "20px",
                overflow: "hidden",
                backgroundColor: style.color1,
                border: `1px solid ${style.color2}`,
                boxSizing: "border-box",
              }}
            >
              <Box
                sx={{
                  px: { xs: 1.25, sm: 1.5, md: 2 },
                  pt: { xs: 1.25, md: 1.5 },
                  pb: { xs: 1, md: 1.25 },
                  backgroundColor: style.darkPanel,
                  borderBottom: "1px solid rgba(255,255,255,0.08)",
                }}
              >
                <Stack spacing={{ xs: 1.1, md: 1.25 }}>
                  <Chip
                    icon={<GridViewRoundedIcon />}
                    label="Plugin Collection"
                    sx={{
                      alignSelf: "flex-start",
                      borderRadius: "5px",
                      fontWeight: 700,
                      color: style.color4,
                      backgroundColor: "rgba(255,255,255,0.06)",
                      height: 28,
                    }}
                  />

                  <Typography
                    variant="h1"
                    sx={{
                      fontSize: { xs: "1.75rem", md: "2.3rem" },
                      fontWeight: 800,
                      lineHeight: 1.05,
                      letterSpacing: "-0.03em",
                      color: style.color4,
                    }}
                  >
                    All Plugins
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      color: style.color3,
                      maxWidth: "720px",
                      fontSize: { xs: "0.95rem", md: "1rem" },
                      lineHeight: 1.65,
                    }}
                  >
                    Professional tools for your project.
                  </Typography>
                </Stack>
              </Box>

              <Box
                sx={{
                  px: { xs: 1, sm: 1.25, md: 1.5 },
                  py: { xs: 1, md: 1.25 },
                }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    backgroundColor: style.darkPanel,
                    borderRadius: "14px",
                    p: { xs: 1, md: 1.1 },
                    border: "1px solid rgba(255,255,255,0.08)",
                    boxSizing: "border-box",
                    overflow: "hidden",
                  }}
                >
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={1}
                    sx={{ width: "50%", minWidth: 0 }}
                  >
                    <TextField
                      fullWidth
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search plugins..."
                      variant="outlined"
                      size="small"
                      sx={{
                        flex: 1.6,
                        minWidth: 0,
                        "& .MuiOutlinedInput-root": {
                          color: style.color4,
                          backgroundColor: "rgba(255,255,255,0.03)",
                          borderRadius: "8px",
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: style.color2,
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: style.color3,
                        },
                        "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: style.accentGreen,
                        },
                        "& .MuiInputBase-input::placeholder": {
                          color: style.color3,
                          opacity: 1,
                        },
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ color: style.color4 }} />
                          </InputAdornment>
                        ),
                      }}
                    />

                    <FormControl
                      size="small"
                      sx={{
                        minWidth: { xs: "100%", md: 220 },
                        "& .MuiOutlinedInput-root": {
                          color: style.color4,
                          backgroundColor: "rgba(255,255,255,0.03)",
                          borderRadius: "8px",
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: style.color2,
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: style.color3,
                        },
                        "& .MuiSvgIcon-root": {
                          color: style.color4,
                        },
                      }}
                    >
                      <InputLabel sx={{ color: style.color4 }}>Sort</InputLabel>
                      <Select
                        value={sortBy}
                        label="Sort"
                        onChange={(e) => setSortBy(e.target.value)}
                        startAdornment={
                          !isMobile ? (
                            <InputAdornment position="start">
                              <TuneRoundedIcon sx={{ color: style.color4, ml: 1 }} />
                            </InputAdornment>
                          ) : undefined
                        }
                      >
                        <MenuItem value="featured">Featured</MenuItem>
                        <MenuItem value="rating">Highest Rated</MenuItem>
                        <MenuItem value="name-asc">Name: A to Z</MenuItem>
                        <MenuItem value="name-desc">Name: Z to A</MenuItem>
                        <MenuItem value="price-low">Price: Low to High</MenuItem>
                        <MenuItem value="price-high">Price: High to Low</MenuItem>
                      </Select>
                    </FormControl>
                  </Stack>
                </Paper>

                <Typography
                  variant="body2"
                  sx={{
                    color: style.color3,
                    mt: 1,
                    mb: 0,
                    ml: 0.25,
                    fontSize: "0.9rem",
                  }}
                >
                  {loading
                    ? "Loading plugins..."
                    : `${filteredProducts.length} plugin${filteredProducts.length === 1 ? "" : "s"}`}
                </Typography>
              </Box>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                width: "100%",
                minWidth: 0,
                borderRadius: "20px",
                overflow: "hidden",
                backgroundColor: style.color1,
                border: `1px solid ${style.color2}`,
                boxSizing: "border-box",
              }}
            >
              <Box sx={{ p: { xs: 1.25, md: 1.5 } }}>
                {loading ? (
                  <Box sx={{ py: 6 }}>
                    <LoadingSpinner />
                  </Box>
                ) : filteredProducts.length === 0 ? (
                  <Box
                    sx={{
                      py: 6,
                      textAlign: "center",
                      color: style.color4,
                    }}
                  >
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 800 }}>
                      No plugins found
                    </Typography>
                    <Typography variant="body2" sx={{ color: style.color3 }}>
                      Try a different search term.
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, minmax(0, 1fr))",
                        md: "repeat(3, minmax(0, 1fr))",
                        lg: "repeat(4, minmax(0, 1fr))",
                      },
                      gap: 2,
                      alignItems: "stretch",
                    }}
                  >
                    {filteredProducts.map((product, index) => (
                      <Box
                        key={product.SKU || product.productURI || index}
                        sx={{
                          minWidth: 0,
                          width: "100%",
                          display: "flex",
                        }}
                      >
                        <Box
                          sx={{
                            width: "100%",
                            minWidth: 0,
                            display: "flex",
                            marginBottom:4,
                          }}
                        >
                          <HomePageProductCard productData={product} />
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Paper>
          </Stack>
        </Box>
      </Box>
    </>
  );
}

// import React, { useState, useEffect, useMemo } from "react";
// import axios from "axios";
// import {
//   Box,
//   Typography,
//   useMediaQuery,
//   TextField,
//   InputAdornment,
//   Select,
//   MenuItem,
//   FormControl,
//   InputLabel,
//   Stack,
//   Paper,
//   Chip,
//   Button,
// } from "@mui/material";
// import SearchIcon from "@mui/icons-material/Search";
// import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
// import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";
// import SubscriptionsRoundedIcon from "@mui/icons-material/SubscriptionsRounded";
// import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
// import { useTheme } from "@mui/material/styles";
// import { Link as RouterLink } from "react-router-dom";

// import { useStyle } from "../../context/styleContext/styleContext";
// import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
// import HomePageProductCard from "../../components/ProductCard/HomePageProductCard";
// import Seo from "../../components/Seo";

// function ProductListSchemaMarkup({ products }) {
//   useEffect(() => {
//     if (!products || products.length === 0) return;

//     const schemaData = {
//       "@context": "https://schema.org",
//       "@type": "CollectionPage",
//       name: "Parish Audio Plugins",
//       url: "https://parishaudio.com/plugins",
//       mainEntity: {
//         "@type": "ItemList",
//         itemListElement: products.map((product, index) => ({
//           "@type": "ListItem",
//           position: index + 1,
//           url: `https://parishaudio.com/products/${product.productURI}`,
//           name: product.name,
//         })),
//       },
//     };

//     const script = document.createElement("script");
//     script.type = "application/ld+json";
//     script.innerHTML = JSON.stringify(schemaData);
//     document.head.appendChild(script);

//     return () => {
//       document.head.removeChild(script);
//     };
//   }, [products]);

//   return null;
// }

// export default function Plugins() {
//   const [height, setHeight] = useState(window.innerHeight);
//   const [dataForAllProducts, setDataForAllProducts] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [searchTerm, setSearchTerm] = useState("");
//   const [priceFilter, setPriceFilter] = useState("all");
//   const [sortBy, setSortBy] = useState("featured");

//   const { style } = useStyle();
//   const theme = useTheme();
//   const isMobile = useMediaQuery(theme.breakpoints.down("md"));

//   useEffect(() => {
//     function handleResize() {
//       setHeight(window.innerHeight);
//     }

//     window.addEventListener("resize", handleResize);
//     return () => window.removeEventListener("resize", handleResize);
//   }, []);

//   useEffect(() => {
//     window.scrollTo({ top: 0, behavior: "auto" });
//   }, []);

//   const getProductInfo = async () => {
//     try {
//       setLoading(true);
//       const { data } = await axios.post("/api/get-products-available-on-website");

//       if (data) {
//         setDataForAllProducts(data);
//       } else {
//         console.log("data error");
//       }
//     } catch (error) {
//       console.log("Error retrieving data...", error);
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     getProductInfo();
//   }, []);

//   const filteredProducts = useMemo(() => {
//     let products = [...dataForAllProducts];

//     const query = searchTerm.trim().toLowerCase();
//     if (query) {
//       products = products.filter((product) => {
//         const searchableText = [
//           product.name,
//           product.shortDescription,
//           product.cardSummary,
//           product.longDescription,
//         ]
//           .filter(Boolean)
//           .join(" ")
//           .toLowerCase();

//         return searchableText.includes(query);
//       });
//     }

//     if (priceFilter === "free") {
//       products = products.filter((product) => Number(product.price) === 0);
//     } else if (priceFilter === "paid") {
//       products = products.filter((product) => Number(product.price) > 0);
//     }

//     if (sortBy === "name-asc") {
//       products.sort((a, b) => a.name.localeCompare(b.name));
//     } else if (sortBy === "name-desc") {
//       products.sort((a, b) => b.name.localeCompare(a.name));
//     } else if (sortBy === "price-low") {
//       products.sort((a, b) => Number(a.price) - Number(b.price));
//     } else if (sortBy === "price-high") {
//       products.sort((a, b) => Number(b.price) - Number(a.price));
//     } else if (sortBy === "rating") {
//       products.sort((a, b) => {
//         const aRating = Number(a.starRating || 0);
//         const bRating = Number(b.starRating || 0);
//         const aCount = Number(a.ratingCount || 0);
//         const bCount = Number(b.ratingCount || 0);

//         if (bRating !== aRating) return bRating - aRating;
//         return bCount - aCount;
//       });
//     } else {
//       products.sort((a, b) => {
//         if (a.productURI === "d1-delay") return -1;
//         if (b.productURI === "d1-delay") return 1;
//         return 0;
//       });
//     }

//     return products;
//   }, [dataForAllProducts, searchTerm, priceFilter, sortBy]);

//   return (
//     <>
//       <Seo
//         title="Audio Plugins | Parish Audio"
//         description="Browse all Parish Audio plugins for mixing, sound design, songwriting, and modern production. Explore free and paid VST3 plugins for Mac and Windows."
//         canonical="https://parishaudio.com/plugins"
//         image="https://parishaudio.com/assets/HeroBanner1.png"
//         imageAlt="Parish Audio plugin collection"
//         ogType="website"
//       />

//       {filteredProducts.length > 0 && (
//         <ProductListSchemaMarkup products={filteredProducts} />
//       )}

//       <Box
//         sx={{
//           width: "100%",
//           maxWidth: "100vw",
//           overflowX: "hidden",
//           backgroundColor: "black",
//           px: { xs: 1.25, sm: 1.5, md: 2.5 },
//           py: { xs: 1.5, md: 2.5 },
//           boxSizing: "border-box",
//         }}
//       >
//         <Box
//           sx={{
//             width: "100%",
//             maxWidth: "1120px",
//             margin: "0 auto",
//             minHeight: height - 130,
//             display: "flex",
//             flexDirection: "column",
//             minWidth: 0,
//           }}
//         >
//           <Stack spacing={{ xs: 2, md: 2.5 }}>
//             <Paper
//               elevation={0}
//               sx={{
//                 width: "100%",
//                 minWidth: 0,
//                 borderRadius: "20px",
//                 overflow: "hidden",
//                 backgroundColor: style.color1,
//                 border: `1px solid ${style.color2}`,
//                 boxSizing: "border-box",
//               }}
//             >
//               <Box
//                 sx={{
//                   px: { xs: 1.5, sm: 2, md: 3 },
//                   py: { xs: 2.25, md: 3 },
//                   backgroundColor: style.darkPanel,
//                   borderBottom: `1px solid ${style.color2}`,
//                 }}
//               >
//                 <Stack
//                   direction={{ xs: "column", md: "row" }}
//                   spacing={{ xs: 2, md: 3 }}
//                   alignItems={{ xs: "flex-start", md: "center" }}
//                   justifyContent="space-between"
//                 >
//                   <Box sx={{ minWidth: 0, flex: 1 }}>
//                     <Chip
//                       icon={<GridViewRoundedIcon />}
//                       label="Plugin Collection"
//                       sx={{
//                         mb: 1,
//                         borderRadius: "5px",
//                         fontWeight: 700,
//                         color: style.color4,
//                         backgroundColor: "rgba(255,255,255,0.06)",
//                       }}
//                     />

//                     <Typography
//                       variant="h1"
//                       sx={{
//                         fontSize: { xs: "2rem", md: "2.7rem" },
//                         fontWeight: 800,
//                         lineHeight: 1.05,
//                         letterSpacing: "-0.03em",
//                         color: style.color4,
//                       }}
//                     >
//                       All Plugins
//                     </Typography>

//                     <Typography
//                       variant="body1"
//                       sx={{
//                         color: style.color3,
//                         mt: 1,
//                         maxWidth: "760px",
//                         fontSize: { xs: "0.98rem", md: "1.05rem" },
//                         lineHeight: 1.7,
//                       }}
//                     >
//                       Browse Parish Audio plugins for mixing, sound design,
//                       songwriting, and modern production. Explore free and paid
//                       VST3 plugins for Mac and Windows.
//                     </Typography>
//                   </Box>

//                   <Stack
//                     direction={{ xs: "column", sm: "row", md: "column" }}
//                     spacing={1}
//                     sx={{ width: { xs: "100%", md: "auto" } }}
//                   >
//                     <Button
//                       component={RouterLink}
//                       to="/subscribe"
//                       startIcon={<SubscriptionsRoundedIcon />}
//                       sx={{
//                         borderRadius: "5px",
//                         textTransform: "none",
//                         fontWeight: 700,
//                         color: style.color4,
//                         backgroundColor: style.accentGreen,
//                         minWidth: { md: 210 },
//                         "&:hover": {
//                           backgroundColor: style.accentGreen,
//                           filter: "brightness(1.05)",
//                         },
//                       }}
//                     >
//                       Start Free Trial
//                     </Button>
//                   </Stack>
//                 </Stack>
//               </Box>

//               <Box
//                 sx={{
//                   px: { xs: 1.25, sm: 1.5, md: 2 },
//                   py: { xs: 1.25, md: 1.5 },
//                 }}
//               >
//                 <Paper
//                   elevation={0}
//                   sx={{
//                     backgroundColor: style.darkPanel,
//                     borderRadius: "14px",
//                     p: { xs: 1.25, md: 1.5 },
//                     border: "1px solid rgba(255,255,255,0.08)",
//                     boxSizing: "border-box",
//                     overflow: "hidden",
//                   }}
//                 >
//                   <Stack
//                     direction={{ xs: "column", md: "row" }}
//                     spacing={1.25}
//                     sx={{ width: "100%", minWidth: 0 }}
//                   >
//                     <TextField
//                       fullWidth
//                       value={searchTerm}
//                       onChange={(e) => setSearchTerm(e.target.value)}
//                       placeholder="Search plugins..."
//                       variant="outlined"
//                       size="small"
//                       sx={{
//                         flex: 1.6,
//                         minWidth: 0,
//                         "& .MuiOutlinedInput-root": {
//                           color: style.color4,
//                           backgroundColor: "rgba(255,255,255,0.03)",
//                           borderRadius: "8px",
//                         },
//                         "& .MuiOutlinedInput-notchedOutline": {
//                           borderColor: style.color2,
//                         },
//                         "&:hover .MuiOutlinedInput-notchedOutline": {
//                           borderColor: style.color3,
//                         },
//                         "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
//                           borderColor: style.accentGreen,
//                         },
//                         "& .MuiInputBase-input::placeholder": {
//                           color: style.color3,
//                           opacity: 1,
//                         },
//                       }}
//                       InputProps={{
//                         startAdornment: (
//                           <InputAdornment position="start">
//                             <SearchIcon sx={{ color: style.color4 }} />
//                           </InputAdornment>
//                         ),
//                       }}
//                     />

//                     <FormControl
//                       size="small"
//                       sx={{
//                         minWidth: { xs: "100%", md: 180 },
//                         "& .MuiOutlinedInput-root": {
//                           color: style.color4,
//                           backgroundColor: "rgba(255,255,255,0.03)",
//                           borderRadius: "8px",
//                         },
//                         "& .MuiOutlinedInput-notchedOutline": {
//                           borderColor: style.color2,
//                         },
//                         "&:hover .MuiOutlinedInput-notchedOutline": {
//                           borderColor: style.color3,
//                         },
//                         "& .MuiSvgIcon-root": {
//                           color: style.color4,
//                         },
//                       }}
//                     >
//                       <InputLabel sx={{ color: style.color4 }}>Price</InputLabel>
//                       <Select
//                         value={priceFilter}
//                         label="Price"
//                         onChange={(e) => setPriceFilter(e.target.value)}
//                       >
//                         <MenuItem value="all">All</MenuItem>
//                         <MenuItem value="free">Free</MenuItem>
//                         <MenuItem value="paid">Paid</MenuItem>
//                       </Select>
//                     </FormControl>

//                     <FormControl
//                       size="small"
//                       sx={{
//                         minWidth: { xs: "100%", md: 220 },
//                         "& .MuiOutlinedInput-root": {
//                           color: style.color4,
//                           backgroundColor: "rgba(255,255,255,0.03)",
//                           borderRadius: "8px",
//                         },
//                         "& .MuiOutlinedInput-notchedOutline": {
//                           borderColor: style.color2,
//                         },
//                         "&:hover .MuiOutlinedInput-notchedOutline": {
//                           borderColor: style.color3,
//                         },
//                         "& .MuiSvgIcon-root": {
//                           color: style.color4,
//                         },
//                       }}
//                     >
//                       <InputLabel sx={{ color: style.color4 }}>Sort</InputLabel>
//                       <Select
//                         value={sortBy}
//                         label="Sort"
//                         onChange={(e) => setSortBy(e.target.value)}
//                         startAdornment={
//                           !isMobile ? (
//                             <InputAdornment position="start">
//                               <TuneRoundedIcon sx={{ color: style.color4, ml: 1 }} />
//                             </InputAdornment>
//                           ) : undefined
//                         }
//                       >
//                         <MenuItem value="featured">Featured</MenuItem>
//                         <MenuItem value="rating">Highest Rated</MenuItem>
//                         <MenuItem value="name-asc">Name: A to Z</MenuItem>
//                         <MenuItem value="name-desc">Name: Z to A</MenuItem>
//                         <MenuItem value="price-low">Price: Low to High</MenuItem>
//                         <MenuItem value="price-high">Price: High to Low</MenuItem>
//                       </Select>
//                     </FormControl>
//                   </Stack>
//                 </Paper>

//                 <Typography
//                   variant="body2"
//                   sx={{
//                     color: style.color3,
//                     mt: 1.5,
//                     mb: 0.5,
//                     ml: 0.25,
//                   }}
//                 >
//                   {loading
//                     ? "Loading plugins..."
//                     : `${filteredProducts.length} plugin${filteredProducts.length === 1 ? "" : "s"} found`}
//                 </Typography>
//               </Box>
//             </Paper>

//             <Paper
//               elevation={0}
//               sx={{
//                 width: "100%",
//                 minWidth: 0,
//                 borderRadius: "20px",
//                 overflow: "hidden",
//                 backgroundColor: style.color1,
//                 border: `1px solid ${style.color2}`,
//                 boxSizing: "border-box",
//               }}
//             >
//               <Box sx={{ p: { xs: 1.25, md: 1.5 } }}>
//                 {loading ? (
//                   <Box sx={{ py: 6 }}>
//                     <LoadingSpinner />
//                   </Box>
//                 ) : filteredProducts.length === 0 ? (
//                   <Box
//                     sx={{
//                       py: 6,
//                       textAlign: "center",
//                       color: style.color4,
//                     }}
//                   >
//                     <Typography variant="h6" sx={{ mb: 1, fontWeight: 800 }}>
//                       No plugins found
//                     </Typography>
//                     <Typography variant="body2" sx={{ color: style.color3 }}>
//                       Try a different search term or filter.
//                     </Typography>
//                   </Box>
//                 ) : (
//                   <Box
//                     sx={{
//                       display: "grid",
//                       gridTemplateColumns: {
//                         xs: "1fr",
//                         sm: "repeat(2, minmax(0, 1fr))",
//                         md: "repeat(3, minmax(0, 1fr))",
//                         lg: "repeat(4, minmax(0, 1fr))",
//                       },
//                       gap: 2,
//                       alignItems: "stretch",
//                     }}
//                   >
//                     {filteredProducts.map((product, index) => (
//                       <Box
//                         key={product.SKU || product.productURI || index}
//                         sx={{
//                           minWidth: 0,
//                           width: "100%",
//                           display: "flex",
//                         }}
//                       >
//                         <Box
//                           sx={{
//                             width: "100%",
//                             minWidth: 0,
//                             display: "flex",
//                           }}
//                         >
//                           <HomePageProductCard productData={product} />
//                         </Box>
//                       </Box>
//                     ))}
//                   </Box>
//                 )}
//               </Box>
//             </Paper>
//           </Stack>
//         </Box>
//       </Box>
//     </>
//   );
// }